import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminSuppliers = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company_name: '',
    address: '',
    is_active: true,
    is_verified: true
  });

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge badge-success">Actif</span>
    ) : (
      <span className="badge badge-danger">Inactif</span>
    );
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = suppliers;

    if (filters.search) {
      filtered = filtered.filter(supplier => {
        const businessName = supplier.business_name || supplier.user?.name || '';
        const contactPerson = supplier.contact_person || '';
        const email = supplier.email || supplier.user?.email || '';
        return (
          businessName.toLowerCase().includes(filters.search.toLowerCase()) ||
          contactPerson.toLowerCase().includes(filters.search.toLowerCase()) ||
          email.toLowerCase().includes(filters.search.toLowerCase())
        );
      });
    }

    if (filters.status) {
      filtered = filtered.filter(supplier =>
        filters.status === 'active' ? (supplier.is_active || supplier.user?.is_active) : (!supplier.is_active && !supplier.user?.is_active)
      );
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = ((a.business_name || a.user?.name) || '').toLowerCase();
            bValue = ((b.business_name || b.user?.name) || '').toLowerCase();
            break;
          case 'email':
            aValue = ((a.email || a.user?.email) || '').toLowerCase();
            bValue = ((b.email || b.user?.email) || '').toLowerCase();
            break;
          case 'status':
            aValue = (a.is_active || a.user?.is_active) ? 1 : 0;
            bValue = (b.is_active || b.user?.is_active) ? 1 : 0;
            break;
          case 'created_at':
            aValue = new Date(a.created_at || 0).getTime();
            bValue = new Date(b.created_at || 0).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredSuppliers(filtered);
    setCurrentPage(1);
  }, [suppliers, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const suppliersData = await adminApi.getSuppliers();
      let suppliersList = [];
      if (Array.isArray(suppliersData)) {
        suppliersList = suppliersData;
      } else if (suppliersData?.data) {
        suppliersList = Array.isArray(suppliersData.data) ? suppliersData.data : suppliersData.data.data || [];
      } else if (suppliersData?.success && suppliersData?.data) {
        suppliersList = Array.isArray(suppliersData.data) ? suppliersData.data : [];
      }
      console.log('Fournisseurs chargés:', suppliersList.length, suppliersList);
      setSuppliers(suppliersList);
    } catch (error) {
      console.error('Erreur lors du chargement des fournisseurs:', error);
      addNotification('Erreur lors du chargement des fournisseurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      company_name: '',
      address: '',
      is_active: true,
      is_verified: true
    });
    setEditingSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const supplierData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company_name: formData.company_name || null,
        is_active: formData.is_active === true || formData.is_active === 'true',
        is_verified: formData.is_verified === true || formData.is_verified === 'true',
      };

      // Ajouter le mot de passe seulement si c'est une création ou s'il est modifié
      if (!editingSupplier || formData.password) {
        if (!formData.password || formData.password.length < 8) {
          addNotification('Le mot de passe doit contenir au moins 8 caractères', 'error');
          return;
        }
        supplierData.password = formData.password;
      }

      let savedSupplier;
      if (editingSupplier) {
        const response = await adminApi.updateSupplier(editingSupplier.id || editingSupplier.user?.id, supplierData);
        savedSupplier = response?.data || response;

        if (!savedSupplier || (!savedSupplier.id && !savedSupplier.user?.id)) {
          throw new Error('Réponse invalide du serveur');
        }

        // Ne pas mettre à jour manuellement, recharger depuis le serveur
        // setSuppliers(prev => prev.map(supplier =>
        //   (supplier.id === editingSupplier.id || supplier.user?.id === editingSupplier.user?.id) ? savedSupplier : supplier
        // ));
        addNotification('Fournisseur modifié avec succès', 'success');
      } else {
        const response = await adminApi.createSupplier(supplierData);
        savedSupplier = response?.data || response;

        if (!savedSupplier || (!savedSupplier.id && !savedSupplier.user?.id)) {
          throw new Error('Réponse invalide du serveur');
        }

        // Ne pas ajouter manuellement, recharger depuis le serveur
        // setSuppliers(prev => [...prev, savedSupplier]);
        addNotification('Fournisseur créé avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      // Attendre un peu pour que le backend termine la transaction
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.user?.name || supplier.contact_person || '',
      email: supplier.email || supplier.user?.email || '',
      password: '', // Ne pas pré-remplir le mot de passe
      phone: supplier.phone || supplier.user?.phone || '',
      company_name: supplier.business_name || '',
      address: supplier.address || '',
      is_active: supplier.is_active !== undefined ? supplier.is_active : (supplier.user?.is_active !== undefined ? supplier.user.is_active : true),
      is_verified: supplier.is_verified !== undefined ? supplier.is_verified : (supplier.user?.is_verified !== undefined ? supplier.user.is_verified : true)
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedSupplier) return;

    try {
      const supplierId = selectedSupplier.id || selectedSupplier.user_id || selectedSupplier.user?.id;
      await adminApi.deleteSupplier(supplierId);
        // Ne pas supprimer manuellement, recharger depuis le serveur
        // setSuppliers(prev => prev.filter(supplier =>
        //   (supplier.id !== selectedSupplier.id) && (supplier.user?.id !== selectedSupplier.user?.id)
        // ));
      setShowDeleteModal(false);
      setSelectedSupplier(null);
      addNotification('Fournisseur supprimé avec succès', 'success');
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      addNotification(errorMessage, 'error');
    }
  };

  const toggleStatus = async (supplier) => {
    try {
      const supplierId = supplier.id || supplier.user_id || supplier.user?.id;
      const currentStatus = supplier.is_active !== undefined ? supplier.is_active : (supplier.user?.is_active !== undefined ? supplier.user.is_active : true);
      await adminApi.updateSupplier(supplierId, {
        ...supplier,
        is_active: !currentStatus
      });
      // const savedSupplier = updatedSupplier.data || updatedSupplier;
      // Ne pas mettre à jour manuellement, recharger depuis le serveur
      // setSuppliers(prev => prev.map(s =>
      //   (s.id === supplier.id || s.user?.id === supplier.user?.id) ? savedSupplier : s
      // ));
      addNotification(`Fournisseur ${!currentStatus ? 'activé' : 'désactivé'} avec succès`, 'success');
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      addNotification('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  // Fonction pour exporter les fournisseurs en CSV
  const handleExport = () => {
    try {
      const suppliersToExport = filteredSuppliers.length > 0 ? filteredSuppliers : suppliers;

      if (suppliersToExport.length === 0) {
        addNotification('Aucun fournisseur à exporter', 'warning');
        return;
      }

      const headers = [
        'ID',
        'Nom',
        'Email',
        'Téléphone',
        'Entreprise',
        'Adresse',
        'Statut',
        'Vérifié',
        'Date de création'
      ];

      const csvRows = suppliersToExport.map(supplier => {
        const row = [
          supplier.id || supplier.user?.id || '',
          `"${((supplier.business_name || supplier.user?.name) || '').replace(/"/g, '""')}"`,
          `"${((supplier.email || supplier.user?.email) || '').replace(/"/g, '""')}"`,
          supplier.phone || supplier.user?.phone || '',
          `"${(supplier.business_name || '').replace(/"/g, '""')}"`,
          `"${(supplier.address || '').replace(/"/g, '""')}"`,
          (supplier.is_active || supplier.user?.is_active) ? 'Actif' : 'Inactif',
          (supplier.is_verified || supplier.user?.is_verified) ? 'Oui' : 'Non',
          supplier.created_at ? new Date(supplier.created_at).toLocaleDateString('fr-FR') : ''
        ];
        return row.join(',');
      });

      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const date = new Date().toISOString().split('T')[0];
      const filename = `fournisseurs_${date}.csv`;
      link.setAttribute('download', filename);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`${suppliersToExport.length} fournisseur(s) exporté(s) avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('Erreur lors de l\'export des fournisseurs', 'error');
    }
  };

  // Fonction pour formater les dates (non utilisée pour le moment)
  // const formatDate = (dateString) => {
  //   if (!dateString) return 'N/A';
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('fr-FR', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   });
  // };

  // Pagination
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSuppliers = filteredSuppliers.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Chargement des fournisseurs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Outils</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Fournisseurs</span>
      </div>

      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Gestion des Fournisseurs</h1>
            <p className="page-subtitle">
              Créer et gérer les fournisseurs de produits agricoles
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="btn btn-primary"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Créer un fournisseur
          </button>
        </div>
      </div>

      {/* Filtres et Actions */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-3 flex-1">
          {/* Recherche */}
          <input
            type="text"
            name="search"
            placeholder="Rechercher un fournisseur..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />

          {/* Filtre par statut */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>

          {/* Bouton pour effacer les filtres */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary text-sm"
          >
            Effacer
          </button>
        </div>
      </div>

      {/* Suppliers DataTable */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Liste des Fournisseurs
            <span className="text-sm text-gray-500 ml-2">
              ({filteredSuppliers.length} fournisseur{filteredSuppliers.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="table-actions">
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              disabled={filteredSuppliers.length === 0}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Fournisseur
                    {sortField === 'name' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Contact</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Produits</th>
                <th>
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Statut
                    {sortField === 'status' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p className="text-lg font-medium">Aucun fournisseur trouvé</p>
                      <p className="text-sm">Les fournisseurs apparaîtront ici</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentSuppliers.map((supplier, index) => {
                  const businessName = supplier.business_name || supplier.user?.name || 'N/A';
                  const contactPerson = supplier.contact_person || supplier.user?.name || 'N/A';
                  const email = supplier.email || supplier.user?.email || 'N/A';
                  const phone = supplier.phone || supplier.user?.phone || 'N/A';
                  const isActive = supplier.is_active !== undefined ? supplier.is_active : (supplier.user?.is_active !== undefined ? supplier.user.is_active : true);
                  // Utiliser un identifiant unique combiné pour éviter les clés dupliquées
                  const supplierId = supplier.id
                    ? `supplier-${supplier.id}`
                    : (supplier.user_id
                      ? `supplier-user-${supplier.user_id}`
                      : (supplier.user?.id
                        ? `supplier-user-${supplier.user.id}`
                        : `supplier-temp-${index}`));

                  return (
                    <tr key={supplierId}>
                      <td>
                        <div className="flex items-center">
                          <div
                            className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold mr-3"
                            style={{ minWidth: '40px', minHeight: '40px' }}
                          >
                            {businessName && businessName.length > 0 ? businessName.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {businessName}
                            </div>
                            {supplier.address && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {supplier.address}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="font-medium">{contactPerson}</td>
                      <td>{email}</td>
                      <td>{phone}</td>
                      <td>
                        <span className="badge badge-info">{supplier.total_products || supplier.products_count || supplier.products?.length || 0}</span>
                      </td>
                      <td>
                        {getStatusBadge(isActive)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Bouton Modifier - Orange/Warning */}
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#f59e0b' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                            title="Modifier"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </button>

                          {/* Bouton Activer/Désactiver - Vert ou Gris */}
                          <button
                            onClick={() => toggleStatus(supplier)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: isActive ? '#10b981' : '#6b7280' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = isActive ? '#059669' : '#4b5563'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = isActive ? '#10b981' : '#6b7280'}
                            title={isActive ? 'Désactiver' : 'Activer'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {isActive ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              )}
                            </svg>
                            {isActive ? 'Désactiver' : 'Activer'}
                          </button>

                          {/* Bouton Supprimer - Rouge/Danger */}
                          <button
                            onClick={() => handleDeleteClick(supplier)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#ef4444' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-4 py-3">
            <div className="text-sm text-gray-700">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredSuppliers.length)} sur {filteredSuppliers.length} fournisseur{filteredSuppliers.length > 1 ? 's' : ''}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <span className="flex items-center px-4 text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'ajout/modification */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingSupplier ? 'Modifier un fournisseur' : 'Créer un fournisseur'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => { setShowAddModal(false); resetForm(); }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="modal-form-group">
                <label className="modal-form-label">Nom complet *</label>
                <input
                  type="text"
                  className="modal-form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Jean Kouassi"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Email *</label>
                <input
                  type="email"
                  className="modal-form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Ex: jean.kouassi@example.com"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">
                  Mot de passe {editingSupplier ? '(laisser vide pour ne pas modifier)' : '*'}
                </label>
                <input
                  type="password"
                  className="modal-form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingSupplier}
                  minLength={8}
                  placeholder="Minimum 8 caractères"
                />
                {editingSupplier && (
                  <p className="text-sm text-gray-500 mt-1">
                    Laissez vide si vous ne souhaitez pas modifier le mot de passe
                  </p>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Téléphone</label>
                <input
                  type="tel"
                  className="modal-form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Ex: +241 07 12 34 56"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Nom de l'entreprise</label>
                <input
                  type="text"
                  className="modal-form-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Ex: Ferme Bio de Kouassi"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Adresse</label>
                <input
                  type="text"
                  className="modal-form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Ex: Libreville, Akanda"
                />
              </div>

              <div className="modal-form-group">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="modal-form-label">Compte actif</span>
                </label>
              </div>

              <div className="modal-form-group">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="modal-form-label">Compte vérifié</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingSupplier ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedSupplier && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Confirmer la suppression</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDeleteModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer le fournisseur <strong>{selectedSupplier.business_name || selectedSupplier.user?.name}</strong> ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cette action est irréversible. Toutes les données associées à ce fournisseur seront également supprimées.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSuppliers;
